import React, { Fragment, useState } from 'react';
import { HiOutlineChevronDown, HiOutlineChevronRight, HiOutlineRefresh } from 'react-icons/hi';

// Renders what the extension reports (frontend_reception/extensions/
// frozen-pick-capture) and the server derives (`effectiveStatus`, which adds
// "offline"). Nothing is re-computed here: the status shown is the server's.
const STATUS_LABEL = {
    ok: 'Healthy',
    unverified: 'Not verified',
    warning: 'Warning',
    error: 'Error',
    auth_error: 'Auth error',
    queue_warning: 'Queue backlog',
    queue_full: 'Queue full',
    offline: 'Offline'
};

const STATUS_HINT = {
    ok: 'Heartbeat is current and an invoice was delivered in the last 24h with no newer failure.',
    unverified: 'Running, but no invoice delivered recently — cannot confirm capture works.',
    warning: 'Running, but the most recent capture or delivery failed.',
    error: 'Not configured, or the invoice grid cannot be found on this PC.',
    auth_error: 'The portal rejected this PC\'s API key. Invoices are held on the PC.',
    queue_warning: 'Invoices have been waiting on this PC for too long.',
    queue_full: 'Hundreds of invoices are waiting on this PC. Nothing is dropped.',
    offline: 'No heartbeat for over 3 minutes — PC off, Chrome closed, or cannot reach the server.'
};

function ago(d) {
    if (!d) return '—';
    const ms = Date.now() - new Date(d).getTime();
    if (ms < 60000) return 'just now';
    if (ms < 3600000) return `${Math.round(ms / 60000)} min ago`;
    if (ms < 86400000) return `${Math.round(ms / 3600000)} h ago`;
    return `${Math.round(ms / 86400000)} d ago`;
}

const absolute = (d) => (d ? new Date(d).toLocaleString('en-GB') : '');

function Time({ value }) {
    return <span title={absolute(value)}>{ago(value)}</span>;
}

function Diagnostics({ h }) {
    const g = h.grid;
    const events = [...(h.events || [])].reverse().slice(0, 10);
    return (
        <div className="fpp-agent-diag">
            <div className="fpp-agent-diag-col">
                <h4>Recent events</h4>
                {events.length === 0 ? <p className="fpp-muted">None reported (extension older than v1.2?)</p> : (
                    <ul className="fpp-agent-events">
                        {events.map((e, i) => (
                            <li key={`${e.type}-${e.at}-${i}`}>
                                <span className="fpp-agent-event-time" title={absolute(e.at)}>{ago(e.at)}</span>
                                <code>{e.type}</code>
                                {e.count > 1 && <span className="fpp-muted"> ×{e.count}</span>}
                                {e.ref && <span className="fpp-muted"> {e.ref}</span>}
                                {e.reason && <div className="fpp-agent-event-reason">{e.reason}</div>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div className="fpp-agent-diag-col">
                <h4>ERP page</h4>
                {!g ? <p className="fpp-muted">No page diagnostics yet.</p> : (
                    <dl className="fpp-agent-facts">
                        <dt>Grid</dt><dd>{g.found ? `found, ${g.rowCount ?? '?'} row(s)` : `not found — ${g.reason || 'unknown'}`}</dd>
                        {g.headers && g.headers.length > 0 && <><dt>Headers</dt><dd>{g.headers.join(' · ')}</dd></>}
                        <dt>Page</dt><dd><code>{g.path || '—'}</code> ({g.readyState || '?'})</dd>
                        <dt>Tables / iframes</dt><dd>{g.tableCount ?? '?'} / {g.iframeCount ?? '?'}</dd>
                        <dt>Update button</dt><dd>{g.updateButtonFound == null ? '?' : g.updateButtonFound ? 'found' : 'not found'}</dd>
                        {(g.tables || []).map((t, i) => (
                            <Fragment key={i}><dt>Table {i + 1}</dt><dd>{(t.headers || []).join(' · ') || '(no header row)'} — {t.rows} row(s)</dd></Fragment>
                        ))}
                        <dt>Checked</dt><dd><Time value={g.at} /></dd>
                    </dl>
                )}
                <h4>Agent</h4>
                <dl className="fpp-agent-facts">
                    <dt>Status detail</dt><dd>{h.detail || '—'}</dd>
                    {h.lastError && <><dt>Last error</dt><dd><code>{h.lastError.code}</code> {h.lastError.reason} (<Time value={h.lastError.at} />)</dd></>}
                    <dt>Oldest queued</dt><dd>{h.oldestQueuedAt ? <Time value={h.oldestQueuedAt} /> : '—'}</dd>
                    <dt>Extension / Chrome</dt><dd>{h.extensionVersion || '≤ 1.1'} / {h.chromeVersion || '?'}</dd>
                    <dt>Status history</dt>
                    <dd>{(h.statusHistory || []).slice(-5).reverse().map((s, i) => (
                        <div key={i}>{STATUS_LABEL[s.status] || s.status} — <Time value={s.at} /></div>
                    ))}</dd>
                </dl>
            </div>
        </div>
    );
}

// Statuses that need someone to look; 'offline' counts too, but is reported
// separately since a PC that is simply switched off is the common case.
const NEEDS_ATTENTION = ['warning', 'error', 'auth_error', 'queue_warning', 'queue_full'];

function summarize(heartbeats) {
    if (heartbeats.length === 0) return { text: 'No billing PCs yet', alert: false };
    const statuses = heartbeats.map((h) => h.effectiveStatus || h.status);
    const parts = [`${heartbeats.length} PC${heartbeats.length === 1 ? '' : 's'}`];
    const attention = statuses.filter((s) => NEEDS_ATTENTION.includes(s)).length;
    const offline = statuses.filter((s) => s === 'offline').length;
    if (attention) parts.push(`${attention} need${attention === 1 ? 's' : ''} attention`);
    if (offline) parts.push(`${offline} offline`);
    if (!attention && !offline) parts.push('all online');
    return { text: parts.join(' · '), alert: attention > 0 };
}

export default function AgentHealth({ heartbeats, updatedAt, onRefresh, defaultExpanded = false }) {
    // Collapsed unless the caller says otherwise — the Agent Health tab opens it.
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [open, setOpen] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState(null);

    const refresh = async () => {
        setRefreshing(true);
        setRefreshError(null);
        try {
            await onRefresh();
        } catch (err) {
            setRefreshError('Refresh failed — check your connection and try again.');
        } finally {
            setRefreshing(false);
        }
    };

    const summary = summarize(heartbeats);

    return (
        <div className={`fpp-agent-health${expanded ? '' : ' collapsed'}`}>
            <div className="fpp-agent-health-head">
                <button
                    type="button"
                    className="fpp-agent-toggle"
                    aria-expanded={expanded}
                    aria-controls="fpp-agent-body"
                    onClick={() => setExpanded((v) => !v)}
                >
                    {expanded ? <HiOutlineChevronDown aria-hidden="true" /> : <HiOutlineChevronRight aria-hidden="true" />}
                    <h3>Agent Health</h3>
                    <span className={`fpp-small ${summary.alert ? 'fpp-warn-text' : 'fpp-muted'}`}>{summary.text}</span>
                </button>
                {expanded && <div className="fpp-agent-refresh">
                    <span className={refreshError ? 'fpp-refresh-error' : 'fpp-muted fpp-small'} role="status" aria-live="polite">
                        {refreshError || (refreshing ? 'Refreshing…' : updatedAt ? `Updated ${new Date(updatedAt).toLocaleTimeString('en-GB')}` : '')}
                    </span>
                    <button
                        type="button"
                        className="fpp-btn fpp-btn-ghost fpp-refresh-btn"
                        onClick={refresh}
                        disabled={refreshing}
                        aria-busy={refreshing}
                    >
                        <HiOutlineRefresh className={refreshing ? 'fpp-spin' : ''} aria-hidden="true" />
                        {refreshing ? 'Refreshing' : 'Refresh'}
                    </button>
                </div>}
            </div>
            {expanded && <div className="fpp-agent-scroll" id="fpp-agent-body">
                <table className="fpp-table fpp-agent-table">
                    <thead>
                        <tr>
                            <th aria-label="Expand" />
                            <th>Agent</th><th>Status</th><th>Heartbeat</th><th>Last capture</th><th>Last success</th>
                            <th>Last error</th><th className="fpp-num">Queue</th><th className="fpp-num">Dead letter</th><th className="fpp-num">Failures</th>
                        </tr>
                    </thead>
                    <tbody>
                        {heartbeats.length === 0 && <tr><td colSpan={10} className="fpp-empty-cell">No billing PCs have checked in yet</td></tr>}
                        {heartbeats.map((h) => {
                            const status = h.effectiveStatus || h.status;
                            const isOpen = open === h.agentId;
                            return (
                                <Fragment key={h.agentId}>
                                    <tr className={`fpp-agent-row${isOpen ? ' open' : ''}`} onClick={() => setOpen(isOpen ? null : h.agentId)}>
                                        <td>{isOpen ? <HiOutlineChevronDown /> : <HiOutlineChevronRight />}</td>
                                        <td>
                                            <div>{h.agentId}</div>
                                            <div className="fpp-muted fpp-small">v{h.extensionVersion || '≤1.1'}</div>
                                        </td>
                                        <td title={STATUS_HINT[status] || ''}>
                                            <span className={`fpp-status-dot fpp-status-${status}`} /> {STATUS_LABEL[status] || status}
                                        </td>
                                        <td><Time value={h.lastSeen} /></td>
                                        <td><Time value={h.lastCaptureAt} /></td>
                                        <td><Time value={h.lastSuccessAt} /></td>
                                        <td className="fpp-agent-error" title={h.lastError ? `${h.lastError.code}: ${h.lastError.reason}` : ''}>
                                            {h.lastError ? <><code>{h.lastError.code}</code> <span className="fpp-muted"><Time value={h.lastError.at} /></span></> : '—'}
                                        </td>
                                        <td className={`fpp-num${h.queueLength > 0 ? ' fpp-warn-text' : ''}`}>{h.queueLength ?? '—'}</td>
                                        <td className={`fpp-num${h.deadLetterCount > 0 ? ' fpp-warn-text' : ''}`}>{h.deadLetterCount ?? '—'}</td>
                                        <td className="fpp-num">{h.consecutiveFailures ?? '—'}</td>
                                    </tr>
                                    {isOpen && <tr className="fpp-agent-detail-row"><td colSpan={10}><Diagnostics h={h} /></td></tr>}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>}
        </div>
    );
}
